import { Flex, useDisclosure } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";

import { Restricted } from "@/app/components/Restricted";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useUnsavedChangesContext } from "@/common/contexts/UnsavedChangesContext";
import { useFormMutationSubmitHandler } from "@/common/hooks/useApiForm";
import { FeatureTargetingChangeConfirmationDialog } from "@/feature/components/FeatureTargetingChangeConfirmationDialog";
import { FormFeatureStagePicker } from "@/feature/components/Form/FormFeatureStagePicker";
import { featureQueryKeys } from "@/feature/data/featureQueryKeys";
import { useFeatureTargetingData } from "@/feature/data/useFeatureTargetingData";
import { useFeatureTargetingUpdateMutation } from "@/feature/data/useFeatureTargetingUpdateMutation";
import { getDefaultNotificationProvidersForTargetingChange } from "@/feature/utils/targetingNotificationDefaults";
import { useTargetingNotificationSettings } from "@/integration/data/useTargetingNotificationSettings";
import { useStagesData } from "@/stage/data/useStagesData";
import { FeatureDetail } from "@reflag/shared/featureAPI";
import {
  UpdateFeatureTargetingBody,
  UpdateFeatureTargetingBodySchema,
} from "@reflag/shared/featureTargetingAPI";
import {
  EnvironmentFeatureTargeting,
  expandEnvironmentTargeting,
} from "@reflag/shared/utils/targetingRule";

export const FeatureSidebarStage = ({
  feature,
}: {
  feature: FeatureDetail;
}) => {
  const { app, env, org } = useEnvironment();
  const queryClient = useQueryClient();
  const confirmSaveDisclosure = useDisclosure();
  const { hasUnsavedChanges } = useUnsavedChangesContext();
  const { data: notificationSettings } = useTargetingNotificationSettings();

  const { data: stages = [] } = useStagesData();
  const { data: currentTargeting = [] } = useFeatureTargetingData(feature.id);

  const createFlagVersionsMutation = useFeatureTargetingUpdateMutation(
    feature.id,
    currentTargeting.map((v) => v.id),
  );

  const expandTargeting = useCallback(
    (targeting: EnvironmentFeatureTargeting[]) =>
      expandEnvironmentTargeting(app.environments, targeting),
    [app],
  );

  const expandedCurrentTargeting = useMemo(
    () => expandTargeting(currentTargeting),
    [expandTargeting, currentTargeting],
  );

  const form = useForm<UpdateFeatureTargetingBody>({
    resolver: zodResolver(UpdateFeatureTargetingBodySchema),
    values: {
      stageId: feature.stage?.id,
      versions: expandedCurrentTargeting,
      changeDescription: "",
    },
  });

  // Watch form values for reactive updates
  const watchedStageId = useWatch({ control: form.control, name: "stageId" });
  const watchedVersions = useWatch({ control: form.control, name: "versions" });

  const onConfirmClick = useFormMutationSubmitHandler(
    form,
    createFlagVersionsMutation,
    (versions, variables) => {
      form.reset({
        stageId: variables.stageId,
        versions: expandTargeting(versions),
        changeDescription: "",
      });

      confirmSaveDisclosure.onClose();
      queryClient.invalidateQueries({
        queryKey: featureQueryKeys.single(app.id, env.id, feature.id),
      });
      queryClient.invalidateQueries({
        queryKey: featureQueryKeys.targeting(app.id, env.id, feature.id),
      });
    },
    {
      successToast: "Stage updated",
      errorToast: "Failed to update stage",
    },
  );

  const onClose = useCallback(() => {
    form.reset();
    confirmSaveDisclosure.onClose();
  }, [confirmSaveDisclosure, form]);

  const onStageChange = useCallback(
    (newStageId: string) => {
      const newVersions = form.getValues("versions");

      form.setValue(
        "notifications",
        getDefaultNotificationProvidersForTargetingChange({
          app,
          feature,
          currentTargeting: expandedCurrentTargeting,
          newTargeting: newVersions,
          currentStageId: feature.stage?.id ?? null,
          newStageId,
          orgIntegrations: org.integrations,
          notificationSettings,
        }),
      );

      confirmSaveDisclosure.onOpen();
    },
    [
      app,
      confirmSaveDisclosure,
      expandedCurrentTargeting,
      feature,
      form,
      notificationSettings,
      org.integrations,
    ],
  );

  return (
    <Restricted alertStyle="none" feature={feature} role="contributor">
      <FormProvider {...form}>
        <Flex as="form" flexDirection="column">
          <FormFeatureStagePicker
            disabledReason="You have unsaved flag targeting changes. Please save or discard them to use this stage picker."
            feature={feature}
            isDisabled={hasUnsavedChanges("targeting")}
            my={-1}
            stageId={watchedStageId ?? ""}
            targetingSnapshot={expandedCurrentTargeting}
            onStageChange={onStageChange}
          />

          <FeatureTargetingChangeConfirmationDialog
            canChangeStage={false}
            currentTargeting={expandedCurrentTargeting}
            feature={feature}
            isLoading={createFlagVersionsMutation.isPending}
            isOpen={confirmSaveDisclosure.isOpen}
            newStageId={watchedStageId ?? null}
            newTargeting={watchedVersions}
            stages={stages}
            targetingSnapshot={expandedCurrentTargeting}
            onCancel={onClose}
            onClose={onClose}
            onConfirm={onConfirmClick}
          />
        </Flex>
      </FormProvider>
    </Restricted>
  );
};
